// Spot-clean policy: everything about the vacuum that is a decision
// rather than a pixel. Consumed by vacuum/shell.jsx and the room-tile
// cards in direction-a.jsx.
//
// The contract this encodes is `ha-vacuum/HANDOFF.md` (symlinked into
// this repo as HANDOFF-ha-vacuum.md). If something here disagrees with
// that file, that file wins — fix it there and re-derive, don't patch
// around it here.
//
// Deliberately a `.js` with NO imports, so it runs under plain `node`
// for tests. Node's ESM loader rejects a `.jsx` extension before it
// reads the file, which is why dev/intent-settle.test.mjs and friends
// can only run under vite-node. Same reasoning as music/volume-drag.js
// and pwa/watch-set.js.

export const VACUUM = {
  entity:      'vacuum.laundry_vacuum',
  mopSelect:   'select.laundry_vacuum_mop_intensity',
  status:      'sensor.laundry_vacuum_status',
  currentRoom: 'sensor.laundry_vacuum_current_room',
  battery:     'sensor.laundry_vacuum_battery',
  error:       'sensor.laundry_vacuum_error',
  mopAttached: 'binary_sensor.laundry_vacuum_mop_attached',
  waterShort:  'binary_sensor.laundry_vacuum_water_shortage',
  dndSwitch:   'switch.laundry_vacuum_do_not_disturb',
  dndBegin:    'time.laundry_vacuum_do_not_disturb_begin',
  dndEnd:      'time.laundry_vacuum_do_not_disturb_end',
};

// Every entity this feature reads. The render filter's watch set is
// DERIVED from MAPPINGS, and none of these appear there, so without
// this list the whole surface silently stops updating — the same class
// of bug as the Office lamp and the Pavilion climate tile. Exported for
// pwa/watch-set.js, which is where it gets folded in.
export const VACUUM_ENTITIES = Object.values(VACUUM);

// The seventeen cleanable rooms, as HA area IDs. `cleaning_area_id` takes
// area IDs, never names and never segment numbers.
//
// Ordered roughly by how often someone will want them rather than
// alphabetically: the open-plan ground floor first, then bedrooms, then
// the small utility rooms, then the boys' wing (mapped 2026-08-20,
// least often wanted — those doors stay shut).
export const AREAS = [
  { id: 'kitchen',        name: 'Kitchen' },
  { id: 'living_room',    name: 'Living Room' },
  { id: 'dining_room',    name: 'Dining Room' },
  { id: 'hall',           name: 'Hall' },
  { id: 'pantry',         name: 'Pantry' },
  { id: 'laundry',        name: 'Laundry' },
  { id: 'master_bed',     name: 'Master Bedroom' },
  { id: 'master_robe',    name: 'Master robe' },
  { id: 'ensuite',        name: 'Master Ensuite' },   // added 2026-08-13 (segment 19)
  { id: 'office',         name: 'Office' },
  { id: 'guest_bedroom',  name: 'Guest room' },
  { id: 'guest_ensuite',  name: 'Guest ensuite' },
  { id: 'powder_room',    name: 'Powder room' },
  { id: 'dylans_room',    name: "Dylan's Room" },     // added 2026-08-20 (segment 17)
  { id: 'connor_s_room',  name: "Connor's Room" },    // added 2026-08-20 (segment 18)
  { id: 'boy_s_robe',     name: "Boy's robe" },       // added 2026-08-20 (segment 20)
  { id: 'boy_s_bathroom', name: "Boy's bathroom" },   // added 2026-08-20 (segment 21)
];

export const AREA_IDS = new Set(AREAS.map((a) => a.id));

// `guest_bedroom` does not follow from its display name, and neither do
// `ensuite` (the MASTER ensuite — the Guest one carries the qualified id)
// or the apostrophe slugs — which are not even consistent with each
// other: `dylans_room` but `connor_s_room`. Resolving an area id by
// slugifying a room name would produce a valid-looking id that silently
// cleans nothing. Look them up here.
export function areaName(id) {
  return AREAS.find((a) => a.id === id)?.name || id;
}

// ── Modes ────────────────────────────────────────────────────────────
//
// There is no mode argument on any service. A mode IS a pair of
// settings, applied before dispatch: suction on the vacuum entity,
// water on the mop_intensity select.
//
// "Both" is ONE simultaneous pass, not two. The device's sequential
// vac-then-mop is carried as `seq_type` on the vendor clean command and
// the integration exposes no entity for it; whether it is reachable via
// send_command is unchecked (HANDOFF §5), so do not rely on either
// answer. Simultaneous is fine here: the main brush leads the mop pads,
// the pads lift on carpet, and both FlexiArms are right-side with the
// brush ahead of the mop, so an edge is swept before it is wiped.
// Settled with Ben 2026-08-10 — do not "fix" this into two runs.
//
// `turbo` is the house suction default since 2026-08-13 (both schedule
// automations were raised from `balanced` — HANDOFF §1). With Carpet
// Boost on device-side, fan_speed governs hard floors only anyway.
export const MODES = [
  { id: 'vacuum', label: 'Vacuum',  fan: 'turbo', mop: 'off',
    hint: 'Suction only. Dry pass.' },
  { id: 'mop',    label: 'Mop',     fan: 'off',   mop: 'medium',
    hint: 'Water only. No suction.' },
  { id: 'both',   label: 'Both',    fan: 'turbo', mop: 'medium',
    hint: 'Vacuums and mops in one pass.' },
];

export function modeById(id) {
  return MODES.find((m) => m.id === id) || MODES[0];
}

// ── Panel rooms → vacuum areas ───────────────────────────────────────
//
// The panel's rooms are amalgams and do not map one-to-one. Only these
// four have any vacuum coverage at all.
//
// Rumpus is downstairs and the robot cannot traverse the stairs; Pool is
// outdoors. Both are PERMANENT exclusions, not mapping gaps, so don't
// carry them as "add later". Master Ensuite was the one genuine gap
// (door shut during mapping) until it was mapped as segment 19 on
// 2026-08-13; it resolves to the `ensuite` area — note the panel room
// id and the HA area id differ.
export const PANEL_ROOM_AREAS = {
  office:         ['office'],
  master_bed:     ['master_bed'],
  master_ensuite: ['ensuite'],
  pavilion:       ['kitchen', 'living_room', 'dining_room', 'pantry', 'laundry'],
};

export function areasForPanelRoom(roomId) {
  return PANEL_ROOM_AREAS[roomId] || [];
}

// ── State reads ──────────────────────────────────────────────────────

// Settle a dispatch on the status sensor entering ANY of these. Which
// one you get depends on the mode, so watching only for the obvious
// `segment_cleaning` leaves a mop run spinning until the timeout.
export const RUNNING_STATES = new Set([
  'segment_cleaning',
  'segment_mopping',
  'segment_clean_mop_cleaning',
  'segment_clean_mop_mopping',
]);

export function isRunningSegment(status) {
  return RUNNING_STATES.has(status);
}

// Broader "the robot is out" test, for the recall button and the
// inert-unless-docked gate. Deliberately NOT the inverse of docked:
// `unknown` and `unavailable` mean we don't know, and the safe reading
// of "don't know" is "don't dispatch".
export function canDispatch(vacuumState) {
  return vacuumState === 'docked';
}

export function isAway(vacuumState) {
  return vacuumState != null
    && !['docked', 'unavailable', 'unknown'].includes(vacuumState);
}

// ── Do Not Disturb ───────────────────────────────────────────────────
//
// DND drops a pending recharge-and-resume rather than deferring it to
// morning (observed 2026-08-07: a run lost its remainder entirely and
// had to be restarted by hand). A spot clean is short enough to be much
// less exposed than a whole-house run, but starting one shortly before
// the window is still a real way to lose half of it.

function minutesOfDay(hhmmss) {
  if (typeof hhmmss !== 'string') return null;
  const m = hhmmss.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return (+m[1]) * 60 + (+m[2]);
}

// Returns 'inside' | 'approaching' | 'clear'. `nowMinutes` is minutes
// since local midnight. The window normally wraps midnight (22:00 →
// 08:00), which is why this is not a simple range test.
export function dndState(nowMinutes, beginStr, endStr, leadMinutes = 60) {
  const begin = minutesOfDay(beginStr);
  const end = minutesOfDay(endStr);
  if (begin == null || end == null || nowMinutes == null) return 'clear';

  const inWindow = begin <= end
    ? (nowMinutes >= begin && nowMinutes < end)
    : (nowMinutes >= begin || nowMinutes < end);
  if (inWindow) return 'inside';

  // Minutes until the window opens, wrapping across midnight.
  const until = (begin - nowMinutes + 1440) % 1440;
  return until <= leadMinutes ? 'approaching' : 'clear';
}

export function nowMinutes(date) {
  return date.getHours() * 60 + date.getMinutes();
}

// ── Dispatch button state ────────────────────────────────────────────
//
// Pure, so the three-way lit/busy/disabled split is testable without
// driving a real dispatch through its timers.
//
// THE RULE THIS ENCODES, and the one the first version broke: **busy is
// lit, never disabled.** `PowerButton` has said so since the AV busy work
// (`const lit = on || busy`) — a control that goes flat and grey the
// instant you press it reads as "that didn't work", which is exactly the
// wrong message when the robot deliberately does nothing for ten seconds
// while the mode reaches it. Busy and disabled both make the button
// unpressable; only one of them should look it.
export function dispatchButton({ ready, count, job }) {
  if (job) {
    // An error is NOT lit. A failed dispatch that still glows like a
    // primary action invites a second press at a robot that may already
    // be moving; the label carries the state instead.
    if (job.error) {
      return {
        label: job.error === 'timeout' ? 'Did not start' : 'Failed',
        busy: false, lit: false, disabled: true, error: true,
      };
    }
    const label = job.phase === 'preparing' ? 'Preparing…'
      : job.phase === 'dispatching' ? 'Sending…'
      : 'Starting…';
    return { label, busy: true, lit: true, disabled: true, error: false };
  }
  if (!ready) return { label: 'Robot is busy', busy: false, lit: false, disabled: true, error: false };
  if (!count)  return { label: 'Pick a room',   busy: false, lit: false, disabled: true, error: false };
  return {
    label: count === 1 ? 'Clean this room' : `Clean ${count} rooms`,
    busy: false, lit: true, disabled: false, error: false,
  };
}

// The pre-roll counts down in the label, so it needs the clock. Split out
// rather than folded into dispatchButton so that function stays pure.
export function preRollSeconds(job, nowMs) {
  if (!job || job.phase !== 'preparing' || job.until == null) return null;
  return Math.max(0, Math.ceil((job.until - nowMs) / 1000));
}
